import React, { useState, useEffect, useRef } from 'react';
import { Space, Select, Tag, Tooltip, Modal, Input, Button, message, Table, DatePicker } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useAmplifierI18n } from '../../i18n/amplifier/useAmplifierI18n';
import { amplifierI18nService } from '../../i18n/amplifier/amplifierI18nService';
import {
    AMPLIFIER_LANGUAGE_OPTIONS,
    localeToProtocol,
    protocolToLocale,
    type AmplifierLocaleId,
} from '../../i18n/amplifier/amplifierLanguage';
import CalibrationModal from './CalibrationModal';
import SystemSyncModal from './SystemSyncModal';
import ConnectionStatusTag from './ConnectionStatusTag';
import { AmplifierProjectData, AmplifierControlData, AmplifierData, ProgramStatusMessage, type AuditLogEntry } from './amplifier';
import { amplifierMqttService } from './AmplifierMqttService';
import type { HamlibControlRequestMessage, UpdateAlarmLogRequestMessage } from './amplifier';
import { AMPLIFIER_ALARM_LABEL_KEYS } from './amplifierAlarmLabels';

type TempAmplifierControlData = AmplifierControlData;

interface TopBarHeaderProps {
    isConnected: boolean;
    isMcuConnected: boolean;
    onAntennaChange: (mode: 'manual' | 'auto') => void;
    onTriggerModeChange: (mode: 'PTT1' | 'PP2' | '输入功率触发') => void;
    onLanguageChange?: (lang: AmplifierLocaleId) => void;
    triggerMode?: number;
    currentTriggerMode?: 'PTT1' | 'PP2' | '输入功率触发';
    amplifierProjectData?: AmplifierProjectData;
    amplifierControlData?: AmplifierControlData;
    onAmplifierControlDataChange?: (controlData: AmplifierControlData) => void;
    deviceId: string;
    amplifierData?: AmplifierData | null;
    programStatusMessage?: ProgramStatusMessage | null;
    hamlibControlRequestMessage?: HamlibControlRequestMessage | null;
    auditLogEntries?: AuditLogEntry[];
    alarmInfoModalOpen: boolean;
    onAlarmInfoModalOpenChange: (open: boolean) => void;
    isRestartPopupOpen?: boolean;
}

export const TopBarHeader: React.FC<TopBarHeaderProps> = ({
    isConnected,
    isMcuConnected,
    onAntennaChange,
    onTriggerModeChange,
    onLanguageChange,
    triggerMode,
    currentTriggerMode,
    amplifierProjectData,
    amplifierControlData,
    onAmplifierControlDataChange,
    deviceId,
    amplifierData,
    programStatusMessage,
    hamlibControlRequestMessage,
    auditLogEntries = [],
    alarmInfoModalOpen,
    onAlarmInfoModalOpenChange,
    isRestartPopupOpen = false,
}) => {
    const { t, language } = useAmplifierI18n();
    const programStatusData = programStatusMessage?.data;
    const hamlibConnected = programStatusData?.hamlib_connection === true;
    const serialConnected = programStatusData?.serial_connection === true;

    useEffect(() => {
        const protocol = programStatusData?.language;
        if (!protocol) return;
        if (amplifierI18nService.shouldIgnoreHeartbeatLanguage(protocol)) return;
        const locale = protocolToLocale(protocol);
        if (locale !== amplifierI18nService.getLanguage()) {
            amplifierI18nService.setLanguage(locale);
        }
    }, [programStatusData?.language]);

    // 添加临时 AmplifierControlData 状态，用于前端临时存储修改的值
    const [tempAmplifierControlData, setTempAmplifierControlData] = useState<TempAmplifierControlData>({
        frequency: 0,
        triggerMode: 0,
        tuneMode: 0,
        power: 0,
        inputAttenuator: 0,
        inputAntenna: 0,
        outputAntenna: 0,
        alarmWord: 0
    });

    // 天线模式状态
    const [antennaMode, setAntennaMode] = useState<'manual' | 'auto'>('manual');

    // 触发模式状态：0=PTT1(默认) 1=PP2 3=输入功率触发（已删除接收模式）
    const [currentTriggerModeState, setCurrentTriggerModeState] = useState<'PTT1' | 'PP2' | '输入功率触发'>('PTT1');
    /** 进入输入功率触发(3) 前的 trigger_mode，用于从 3 切回时恢复 PTT1(0) 或 PTT2(1) */
    const triggerModeBeforePowerRef = useRef<number>(0);
    const [systemTime, setSystemTime] = useState<string>(() => {
        const now = new Date();
        return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
    });
    const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
    const [isCalibrationModalVisible, setIsCalibrationModalVisible] = useState(false);
    const [isSystemSyncModalVisible, setIsSystemSyncModalVisible] = useState(false);
    const [password, setPassword] = useState('');
    const [alarmLogStartTime, setAlarmLogStartTime] = useState<Dayjs | null>(() => dayjs().subtract(30, 'minute'));
    const [alarmLogEndTime, setAlarmLogEndTime] = useState<Dayjs | null>(() => dayjs());
    const [alarmLogPageSize, setAlarmLogPageSize] = useState<number>(10);
    const [alarmLogType, setAlarmLogType] = useState<'audit' | 'alarm'>('alarm');
    const hasRequestedInitialAlarmLogRef = useRef(false);
    /** 报警日志列表（由后端/MQTT 回包通过 setAlarmLogDataSource 填充），是否分页由实际条数决定，与前端 count 选择无关 */
    const [alarmLogDataSource, setAlarmLogDataSource] = useState<Array<{ key: number | string; time: string; level: string; message: string }>>([]);
    const [alarmLogCurrentPage, setAlarmLogCurrentPage] = useState<number>(1);

    const alarmLogTotalPages = alarmLogDataSource.length > 10 ? Math.ceil(alarmLogDataSource.length / 10) : 1;
    useEffect(() => {
        setAlarmLogCurrentPage((prev) => {
            const totalPages = alarmLogDataSource.length > 10 ? Math.ceil(alarmLogDataSource.length / 10) : 1;
            return Math.min(prev, totalPages) || 1;
        });
    }, [alarmLogDataSource.length]);

    useEffect(() => {
        const rows = auditLogEntries.map((entry, index) => ({
            key: `${entry.time}-${index}`,
            time: entry.time ?? '',
            level: entry.level ?? '',
            message: entry.message ?? ''
        }));
        setAlarmLogDataSource(rows);
        setAlarmLogCurrentPage(1);
    }, [auditLogEntries]);

    useEffect(() => {
        const timer = setInterval(() => {
            const now = new Date();
            const timeString = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
            setSystemTime(timeString);
        }, 1000);

        return () => clearInterval(timer);
    }, []);



    // 当 amplifierControlData 变化时，更新临时控制数据
    useEffect(() => {
        if (amplifierControlData) {
            setTempAmplifierControlData(amplifierControlData);
        }
    }, [amplifierControlData]);

    // 根据 triggerMode 数字同步触发模式显示：0=PTT1 1=PP2 3=输入功率触发（2 兼容显示为 PP2）
    useEffect(() => {
        if (triggerMode !== undefined) {
            const numberToLabel: { [key: number]: 'PTT1' | 'PP2' | '输入功率触发' } = {
                0: 'PTT1',
                1: 'PP2',
                2: 'PP2',
                3: '输入功率触发'
            };
            setCurrentTriggerModeState(numberToLabel[triggerMode] ?? 'PTT1');
        }
    }, [triggerMode]);

    const handleLanguageChange = async (value: AmplifierLocaleId) => {
        const protocol = localeToProtocol(value);
        amplifierI18nService.markUserLanguageChange(protocol);
        amplifierI18nService.setLanguage(value);
        onLanguageChange?.(value);
        try {
            await amplifierMqttService.sendRemoteControlDelta(deviceId, { language: protocol });
        } catch (error) {
            console.error('TopBarHeader: 语言切换 MQTT 发送失败:', error);
        }
    };

    const triggerModeDisplay = (mode: 'PTT1' | 'PP2' | '输入功率触发') =>
        mode === '输入功率触发' ? t('输入功率触发') : mode;

    const handleAntennaChange = async () => {
        // 切换天线模式
        const newMode = antennaMode === 'manual' ? 'auto' : 'manual';
        setAntennaMode(newMode);

        // 调用父组件的回调函数
        onAntennaChange(newMode);

        // 发送指令
        try {
            console.log('TopBarHeader: 切换天线模式:', newMode);
        } catch (error) {
            console.error('TopBarHeader: 天线模式切换失败:', error);
        }
    };

    const handleTriggerModeChange = async () => {
        // PTT1(0)/PTT2(1) ↔ 输入功率触发(3)；进入 3 前记住当前模式，退出 3 时恢复
        let modeValue: number;
        if (triggerMode === 3) {
            modeValue = triggerModeBeforePowerRef.current;
        } else {
            const current = triggerMode ?? 0;
            triggerModeBeforePowerRef.current = current === 2 ? 1 : current;
            modeValue = 3;
        }

        const labelMap: Record<number, 'PTT1' | 'PP2' | '输入功率触发'> = {
            0: 'PTT1',
            1: 'PP2',
            2: 'PP2',
            3: '输入功率触发',
        };
        onTriggerModeChange(labelMap[modeValue] ?? 'PTT1');

        try {
            await amplifierMqttService.sendAmplifierControlDelta(deviceId, {
                triggerMode: modeValue,
            });
            console.log('TopBarHeader: 触发模式更新指令发送成功:', { triggerMode: modeValue });
        } catch (error) {
            console.error('TopBarHeader: 触发模式更新指令发送失败:', error);
        }
        // 不在此处 setCurrentTriggerModeState / setTempAmplifierControlData，等 MQTT 回包后由 triggerMode/amplifierData 驱动渲染，避免与设备不同步
    };

    const getConnectionStatusLabel = (connected: boolean) => {
        return connected ? t('Connected') : t('Disconnected');
    };

    const getConnectionStatusColor = (connected: boolean) => {
        return connected ? 'green' : 'red';
    };

    const handleCalibrationButtonClick = () => {
        setIsPasswordModalVisible(true);
        setPassword('');
    };

    const handlePasswordConfirm = () => {
        if (password === '666') {
            setIsPasswordModalVisible(false);
            setIsCalibrationModalVisible(true);
            message.success(t('密码验证成功'));
        } else {
            message.error(t('密码错误，请重试'));
            setPassword('');
        }
    };

    const handlePasswordCancel = () => {
        setIsPasswordModalVisible(false);
        setPassword('');
    };

    const handleCalibrationConfirm = () => {
        setIsCalibrationModalVisible(false);
        message.success(t('工程参数设置已保存'));
    };

    const handleCalibrationCancel = () => {
        setIsCalibrationModalVisible(false);
    };

    const buildAuditLogMessageId = (): string => {
        if (typeof globalThis.crypto?.randomUUID === 'function') {
            return globalThis.crypto.randomUUID();
        }
        return `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
    };

    const sendAlarmLogQuery = (
        type: 'audit' | 'alarm',
        startTime: Dayjs | null,
        endTime: Dayjs | null,
        count: number,
        showSuccessMessage = true,
    ) => {
        const start = startTime ? startTime.format('YYYY-MM-DD HH:mm:ss') : '';
        const end = endTime ? endTime.format('YYYY-MM-DD HH:mm:ss') : '';
        const logCommand = type === 'audit' ? 'update_audit_log' : 'update_alarm_log';
        const logLabel = type === 'audit' ? t('审计日志') : t('报警日志');
        const msg: UpdateAlarmLogRequestMessage = {
            msg_id: buildAuditLogMessageId(),
            cmd: logCommand,
            timestamp: Math.floor(Date.now() / 1000),
            data: { count, start_time: start, end_time: end }
        };
        amplifierMqttService.sendJsonCommand(deviceId, msg).then(() => {
            if (showSuccessMessage) {
                message.success(type === 'audit' ? t('更新审计日志指令已发送') : t('更新报警日志指令已发送'));
            }
        }).catch((err) => {
            console.error(`更新${logLabel}发送失败:`, err);
            message.error(type === 'audit' ? t('更新审计日志发送失败') : t('更新报警日志发送失败'));
        });
    };

    const handleUpdateLog = () => {
        sendAlarmLogQuery(alarmLogType, alarmLogStartTime, alarmLogEndTime, alarmLogPageSize);
    };

    const handleOpenAlarmInfo = () => {
        if (!hasRequestedInitialAlarmLogRef.current && alarmLogDataSource.length === 0) {
            const end = dayjs();
            const start = end.subtract(30, 'minute');
            hasRequestedInitialAlarmLogRef.current = true;
            setAlarmLogType('alarm');
            setAlarmLogPageSize(10);
            setAlarmLogStartTime(start);
            setAlarmLogEndTime(end);
            sendAlarmLogQuery('alarm', start, end, 10, false);
        }
        onAlarmInfoModalOpenChange(true);
    };

    return (
        <div className="amplifier-header">
            <div className="amplifier-header__left">
                <Select
                    size="small"
                    value={language}
                    onChange={handleLanguageChange}
                    options={AMPLIFIER_LANGUAGE_OPTIONS}
                    style={{ width: 110 }}
                    className="amplifier-topbar-lang-select"
                    variant="borderless"
                    styles={{ popup: { root: { zIndex: 12000 } } }}
                />
                <Tag className={hamlibConnected ? 'amp-sync-tag amp-sync-tag--online' : 'amp-sync-tag amp-sync-tag--offline'}>
                    {hamlibConnected ? t('Hamlib: Connected') : `Hamlib: ${t('Disconnected')}`}
                </Tag>
                <Tag className={serialConnected ? 'amp-sync-tag amp-sync-tag--online' : 'amp-sync-tag amp-sync-tag--offline'}>
                    {serialConnected ? `MUC: ${t('Connected')}` : t('MUC: 未连接')}
                </Tag>
            </div>

            <Space size="small">
                {/* <Button
                    size="small"
                    type="primary"
                    onClick={handleCalibrationButtonClick}
                    style={{
                        backgroundColor: '#1890ff',
                        borderColor: '#1890ff'
                    }}
                >
                    工程参数设置
                </Button> */}

                <div className="amp-hdr-control">
                    <span className="amplifier-header__label">{t('输出天线：')}</span>
                    <Button
                        size="small"
                        className={`amp-hdr-btn amp-hdr-btn--antenna-mode ${antennaMode === 'auto' ? 'amp-hdr-btn--green' : 'amp-hdr-btn--accent'}`}
                        onClick={handleAntennaChange}
                        style={{ width: 100 }}
                    >
                        {antennaMode === 'manual' ? t('手动选择') : t('自动选择')}
                    </Button>
                </div>

                <div className="amp-hdr-control">
                    <span className="amplifier-header__label">{t('触发模式：')}</span>
                    <Button
                        size="small"
                        className="amp-hdr-btn amp-hdr-btn--green amp-hdr-btn--trigger-mode"
                        onClick={handleTriggerModeChange}
                        style={{ width: 120 }}
                    >
                        {triggerModeDisplay(currentTriggerModeState)}
                    </Button>
                </div>

                <div className="amp-hdr-control">
                    <span className="amplifier-header__time">{systemTime}</span>
                    <Button
                        size="small"
                        className="amp-hdr-btn amp-hdr-btn--green"
                        onClick={() => setIsSystemSyncModalVisible(true)}
                    >
                        {t('系统同步')}
                    </Button>
                    <span className="amplifier-header__label">{t('时间')}</span>
                    <Button
                        size="small"
                        className={
                            (amplifierData?.alarmWord ?? 0) !== 0
                                ? 'amp-hdr-btn amp-hdr-btn--danger amp-hdr-btn--alarm-blink'
                                : 'amp-hdr-btn amp-hdr-btn--green'
                        }
                        onClick={handleOpenAlarmInfo}
                    >
                        {t('报警信息')}
                    </Button>
                </div>
            </Space>

            <Modal
                title={t('密码验证')}
                open={isPasswordModalVisible}
                onOk={handlePasswordConfirm}
                onCancel={handlePasswordCancel}
                okText={t('确认')}
                cancelText={t('取消')}
            >
                <div style={{ marginBottom: '16px' }}>
                    <Input
                        type="password"
                        placeholder={t('请输入密码')}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onPressEnter={handlePasswordConfirm}
                        maxLength={10}
                        style={{ width: '100%' }}
                    />
                </div>
                <div style={{ color: '#666', fontSize: '12px' }}>
                    {t('提示：密码为 666')}
                </div>
            </Modal>

            <CalibrationModal
                isOpen={isCalibrationModalVisible}
                onClose={handleCalibrationCancel}
                onConfirm={handleCalibrationConfirm}
                amplifierProjectData={amplifierProjectData}
                deviceId={deviceId}
            />

            <SystemSyncModal
                isOpen={isSystemSyncModalVisible}
                onClose={() => setIsSystemSyncModalVisible(false)}
                deviceId={deviceId}
                amplifierProjectData={amplifierProjectData}
                amplifierData={amplifierData}
                programStatusMessage={programStatusMessage}
                onProgramStatusSave={(delta) => amplifierMqttService.sendProgramStatusDelta(deviceId, delta)}
                onDeviceUpdate={(type) =>
                    amplifierMqttService.sendRemoteControlDelta(deviceId, { update_check: type })
                }
                hamlibControlRequestMessage={hamlibControlRequestMessage}
                onHamlibControlSave={(delta) => amplifierMqttService.sendHamlibControlDelta(deviceId, delta)}
                isRestartPopupOpen={isRestartPopupOpen}
            />

            {/* 报警信息弹窗 */}
            <Modal
                open={alarmInfoModalOpen}
                onCancel={() => onAlarmInfoModalOpenChange(false)}
                footer={null}
                width={1040}
                rootClassName="alarm-info-modal"
                styles={{
                    body: { padding: 0 },
                    header: { display: 'none' }
                }}
            >
                <div className="alarm-info-shell">
                    {/* 左侧：启用报警信息 */}
                    <div className="alarm-info-side">
                        <div className="alarm-info-section-title">
                            <span className="alarm-info-section-title__dot" />
                            <span>{t('启用报警信息')}</span>
                        </div>
                        <ul className="alarm-info-list">
                            {AMPLIFIER_ALARM_LABEL_KEYS.map((labelKey, i) => {
                                const alarmWord = amplifierData?.alarmWord ?? 0;
                                const isAlarm = (alarmWord & (1 << i)) !== 0;
                                return (
                                    <li key={i} className={isAlarm ? 'alarm-info-list__item alarm-info-list__item--active' : 'alarm-info-list__item'}>
                                        <span className="alarm-info-list__index">{String(i + 1).padStart(2, '0')}</span>
                                        <span className="alarm-info-list__label">{t(labelKey)}</span>
                                    </li>
                                );
                            })}
                        </ul>
                        <div className="alarm-info-side__footer">
                            <Button
                                type="default"
                                className="alarm-info-btn alarm-info-btn--danger"
                                onClick={() => {
                                    amplifierMqttService.sendClearAlarm(deviceId).then(() => {
                                        message.success(t('清除报警指令已发送'));
                                    }).catch((err) => {
                                        console.error('清除报警发送失败:', err);
                                        message.error(t('清除报警发送失败'));
                                    });
                                }}
                            >
                                {t('清除报警')}
                            </Button>
                        </div>
                    </div>
                    {/* 右侧：日志查询 */}
                    <div className="alarm-info-main">
                        <div className="alarm-info-main__header">
                            <div>
                                <div className="alarm-info-title">{alarmLogType === 'audit' ? t('审计日志') : t('报警日志')}</div>
                                <div className="alarm-info-subtitle">{t('按日志类型与精确时间范围查询设备事件')}</div>
                            </div>
                            <div className="alarm-info-count">{t('共 {count} 条', { count: alarmLogDataSource.length })}</div>
                        </div>
                        <div className="alarm-info-toolbar">
                            <div className="alarm-info-segmented">
                                <Button
                                    size="small"
                                    type={alarmLogType === 'audit' ? 'primary' : 'default'}
                                    className={alarmLogType === 'audit' ? 'alarm-info-tab alarm-info-tab--active' : 'alarm-info-tab'}
                                    onClick={() => setAlarmLogType('audit')}
                                >
                                    {t('审计日志')}
                                </Button>
                                <Button
                                    size="small"
                                    type={alarmLogType === 'alarm' ? 'primary' : 'default'}
                                    className={alarmLogType === 'alarm' ? 'alarm-info-tab alarm-info-tab--active' : 'alarm-info-tab'}
                                    onClick={() => setAlarmLogType('alarm')}
                                >
                                    {t('报警日志')}
                                </Button>
                            </div>
                            <label className="alarm-info-field">
                                <span>{t('开始时间')}</span>
                                <DatePicker
                                    format="YYYY-MM-DD HH:mm:ss"
                                    showTime={{ format: 'HH:mm:ss' }}
                                    value={alarmLogStartTime}
                                    onChange={(v) => setAlarmLogStartTime(v)}
                                    placeholder={t('选择开始时间')}
                                    className="alarm-info-picker"
                                    allowClear
                                />
                            </label>
                            <label className="alarm-info-field">
                                <span>{t('结束时间')}</span>
                                <DatePicker
                                    format="YYYY-MM-DD HH:mm:ss"
                                    showTime={{ format: 'HH:mm:ss' }}
                                    value={alarmLogEndTime}
                                    onChange={(v) => setAlarmLogEndTime(v)}
                                    placeholder={t('选择结束时间')}
                                    className="alarm-info-picker"
                                    allowClear
                                />
                            </label>
                            <label className="alarm-info-field alarm-info-field--count">
                                <span>{t('数量')}</span>
                                <Select
                                    value={alarmLogPageSize}
                                    onChange={setAlarmLogPageSize}
                                    options={[
                                        { value: 5, label: '5' },
                                        { value: 10, label: '10' },
                                        { value: 20, label: '20' },
                                        { value: 50, label: '50' },
                                        { value: 100, label: '100' }
                                    ]}
                                    className="alarm-info-select"
                                />
                            </label>
                        </div>
                        <div className="alarm-info-table-wrap">
                            <Table
                                size="small"
                                dataSource={alarmLogDataSource}
                                columns={[
                                    { title: t('时间'), dataIndex: 'time', key: 'time', width: 170, render: (v: string) => <span className="alarm-info-table__time">{v}</span> },
                                    { title: t('等级'), dataIndex: 'level', key: 'level', width: 80, render: (v: string) => <span className="alarm-info-table__level">{v}</span> },
                                    { title: t('消息'), dataIndex: 'message', key: 'message', ellipsis: true, render: (v: string) => <span className="alarm-info-table__message">{v}</span> }
                                ]}
                                pagination={alarmLogDataSource.length > 10 ? {
                                    total: alarmLogDataSource.length,
                                    pageSize: 10,
                                    current: Math.min(alarmLogCurrentPage, alarmLogTotalPages) || 1,
                                    onChange: (page: number) => setAlarmLogCurrentPage(page),
                                    showSizeChanger: false,
                                    showTotal: (total: number) => t('共 {count} 条', { count: total }),
                                    showQuickJumper: true,
                                    showPrevNextJumpers: true
                                } : false}
                                className="alarm-log-table"
                            />
                        </div>
                    </div>
                </div>
                {/* 底部操作按钮 */}
                <div className="alarm-info-footer">
                    <Button
                        type="default"
                        className="alarm-info-btn"
                        onClick={handleUpdateLog}
                    >
                        {t('更新')}
                    </Button>
                    <Button
                        type="default"
                        className="alarm-info-btn"
                        onClick={() => onAlarmInfoModalOpenChange(false)}
                    >
                        {t('取消')}
                    </Button>
                    <Button
                        type="primary"
                        className="alarm-info-btn alarm-info-btn--primary"
                        onClick={() => onAlarmInfoModalOpenChange(false)}
                    >
                        {t('确认')}
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default TopBarHeader;